import { hotRank, tagWall, type RankItem, type TagItem } from '@/mocks/home';

const toneClass = (tone: TagItem['tone']) => {
  if (tone === 'fire') return 'bg-primary-100 text-primary-800 border-primary-200 hover:border-primary-400';
  if (tone === 'amber') return 'bg-accent-100 text-accent-800 border-accent-200 hover:border-accent-400';
  return 'bg-background-200 text-foreground-700 border-background-300 hover:border-secondary-400';
};

const trendIcon = (trend: RankItem['trend']) => {
  if (trend === 'up') return { icon: 'ri-arrow-up-line', cls: 'text-primary-600' };
  if (trend === 'down') return { icon: 'ri-arrow-down-line', cls: 'text-secondary-500' };
  return { icon: 'ri-subtract-line', cls: 'text-secondary-400' };
};

export default function RankWall() {
  return (
    <section id="rank" className="relative w-full bg-background-50 py-12 md:py-16">
      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-6 px-4 md:px-6 lg:grid-cols-12">
        {/* 榜单 */}
        <div className="lg:col-span-7">
          <div className="mb-5 flex items-end justify-between">
            <div>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-widest text-primary-600">
                <i className="ri-fire-line" /> HOT 100
              </span>
              <h2 className="mt-1 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
                今日瓜榜 · 热度飙升
              </h2>
            </div>
            <button
              type="button"
              className="hidden cursor-pointer items-center gap-1 whitespace-nowrap rounded-md border border-background-300 px-3 py-1.5 text-sm text-foreground-600 transition-colors hover:border-secondary-400 hover:text-foreground-900 sm:flex"
            >
              换一屏 <i className="ri-refresh-line" />
            </button>
          </div>

          <div className="space-y-2.5">
            {hotRank.map((item) => {
              const tr = trendIcon(item.trend);
              const isTop = item.rank <= 3;
              return (
                <a
                  key={item.rank}
                  href="#feed"
                  className="group flex cursor-pointer items-center gap-3 rounded-md border border-transparent px-3 py-3 transition-colors hover:border-background-200 hover:bg-background-100"
                >
                  <span
                    className={`w-8 shrink-0 text-center font-heading text-xl font-black ${
                      isTop ? 'text-primary-600' : 'text-secondary-500 group-hover:text-foreground-700'
                    }`}
                  >
                    {item.rank}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-semibold text-foreground-900">
                      {item.title}
                    </span>
                  </span>
                  <span className="hidden w-28 shrink-0 items-center gap-2 sm:flex">
                    <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-background-200">
                      <span
                        className="block h-full rounded-full bg-gradient-to-r from-primary-500 to-accent-500"
                        style={{ width: `${Math.min(100, (item.heat / 982) * 100)}%` }}
                      />
                    </span>
                    <span className="text-xs text-foreground-600">{item.heat}</span>
                  </span>
                  <span
                    className={`hidden shrink-0 rounded-md px-2 py-1 text-xs font-semibold md:inline-flex ${
                      item.tag === '大瓜' || item.tag === '猛料'
                        ? 'bg-primary-100 text-primary-800'
                        : item.tag === '压轴' || item.tag === '高能'
                          ? 'bg-accent-100 text-accent-800'
                          : 'bg-background-200 text-foreground-700'
                    }`}
                  >
                    {item.tag}
                  </span>
                  <span className={`flex w-6 shrink-0 items-center justify-center ${tr.cls}`}>
                    <i className={tr.icon} />
                  </span>
                </a>
              );
            })}
          </div>
        </div>

        {/* 话题墙 */}
        <div className="lg:col-span-5">
          <div className="mb-5">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold tracking-widest text-accent-600">
              <i className="ri-price-tag-3-line" /> TOPIC WALL
            </span>
            <h2 className="mt-1 font-heading text-2xl font-black text-foreground-950 md:text-3xl">
              高能话题墙
            </h2>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-100/60 p-5 md:p-6">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-foreground-600">点击话题即可围观对应高能现场</span>
              <span className="flex items-center gap-1 text-xs text-accent-600">
                <i className="ri-flashlight-line" /> 实时热度
              </span>
            </div>
            <div className="mt-5 flex flex-wrap gap-2.5">
              {tagWall.map((tag) => {
                const size =
                  tag.heat >= 90
                    ? 'text-sm px-4 py-2'
                    : tag.heat >= 70
                      ? 'text-sm px-3.5 py-2'
                      : 'text-xs px-3 py-1.5';
                return (
                  <button
                    key={tag.name}
                    type="button"
                    className={`whitespace-nowrap cursor-pointer rounded-full border font-semibold transition-all hover:-translate-y-0.5 ${size} ${toneClass(tag.tone)}`}
                  >
                    <i
                      className={`mr-1 ${tag.tone === 'fire' ? 'ri-fire-fill text-primary-500' : tag.tone === 'amber' ? 'ri-flashlight-fill text-accent-500' : 'ri-hashtag text-secondary-500'}`}
                    />
                    {tag.name}
                    <span className="ml-1 opacity-60">{tag.heat}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}